<template>
  <form class="email-form" @submit.prevent="onSubmitAsync">
    <div
      v-if="callerType == EmailFormCallerType.RfrAssignmentsRequest"
      class="inline-form-control"
    >
      <label for="email-recipient">To:</label>
      <input
        type="text"
        id="email-recipient"
        name="email-recipient"
        v-model="emailRecipient"
        readonly
        disabled
      />
    </div>
    <div
      v-if="callerType == EmailFormCallerType.RfrAssignmentsRequest"
      class="inline-form-control"
    >
      <label for="subject">Subject:</label>
      <input
        type="text"
        id="subject"
        name="subject"
        v-model="subject"
        readonly
        disabled
      />
    </div>
    <span v-if="holidayMessage" class="holiday-message">
      {{ holidayMessage }}
    </span>
    <div class="form-control">
      <label for="message">Message:</label>
      <textarea
        id="message"
        class="message-textarea"
        name="message"
        v-model="message"
        rows="10"
        placeholder="Enter your message here..."
        data-cy="message-textarea"
      ></textarea>
    </div>

    <div class="padding-between">
      <header class="form-header">
        <span data-cy="documentation-header-title">Add Documents</span>
      </header>

      <FileUpload v-model="uploadedDocs" />
    </div>

    <Button
      type="submit"
      theme-color="primary"
      :disabled="message.length == 0 || formSubmitting"
      data-cy="submit-button"
      >Submit</Button
    >
  </form>
</template>

<script setup lang="ts">
import { useMainStore } from "@/stores/useMainStore";
import { EmailFormCallerType } from "@/Domain/EmailFormCallerType";
import { onBeforeMount, ref, watchEffect } from "vue";
import type { UploadFileInfo } from "@progress/kendo-vue-upload";
import { Button } from "@progress/kendo-vue-buttons";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { type API, api } from "@/api";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import {
  getAttachmentFilesAsync,
  validateTotalUploadSize,
} from "@/shared/helpers/fileHelper";
import type { PersonJobTb } from "@/Domain/PersonJobTb";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { CacheService } from "@/shared/services/CacheService";
import FileUpload from "@/shared/ui/inputs/FileUpload.vue";

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  callerType: EmailFormCallerType;
  emailRecipient?: string;
  companyName?: string;
  plantName?: string;
  assignmentId?: number;
}>();

const emit = defineEmits(["formSubmitted"]);

const subject = ref<string>("");
const emailRecipient = ref<string>(props.emailRecipient ?? "");
const message = ref<string>("");
const uploadedDocs = ref<UploadFileInfo[]>([]);
const formSubmitting = ref<boolean>(false);
const holidayMessage = ref<string>("");
const personJob = ref<PersonJobTb | null>(null);

const updateSubject = function () {
  if (props.callerType == EmailFormCallerType.RfrAssignmentsRequest)
    subject.value = `OUDirect RFR Assignments Message from ${
      personJob.value?.personTb.FormattedName ?? ""
    } (${main.userLogin}) [Re: ${props.companyName} / ${props.plantName}]`;
  else subject.value = "";
};

updateSubject();

const updateHolidayMessageAsync = async function () {
  return handleResponsePromiseAsync(
    api.get("GetHolidayMessage", {}),
    (data) => {
      holidayMessage.value = data;
    },
    "IngredientsCartModal.vue",
    "updateHolidayMessageAsync"
  );
};

const getCurrentPersonJobAsync = async function () {
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
  };

  const personJobPromise = CacheService.getResponseAsync(
    createCacheKey(requestParams, "GetCurrentPersonJob"),
    () => api.get("GetCurrentPersonJob", requestParams)
  );

  return handleResponsePromiseAsync(
    personJobPromise,
    (response) => {
      personJob.value = response;
      updateSubject();
    },
    "EmailFormModal.vue",
    "getCurrentPersonJobAsync"
  );
};

const onSubmitAsync = async function () {
  if (formSubmitting.value) return;

  formSubmitting.value = true;

  // Validate total files size
  if (!validateTotalUploadSize(uploadedDocs.value)) {
    formSubmitting.value = false;
    return;
  }

  const files = await getAttachmentFilesAsync(uploadedDocs.value);

  let endpoint: keyof API = "SendDashboardEmail";
  if (props.callerType == EmailFormCallerType.RfrAssignmentsRequest)
    endpoint = "SendRfrEmail";

  const sendEmailPromise = api.post(endpoint, {
    UserLogin: main.userLogin,
    CompanyLogin: main.companyLogin,
    Message: message.value,
    AssignmentId: props.assignmentId || -1,
    Files: files,
  });

  await handleResponsePromiseAsync(
    sendEmailPromise,
    () => {
      notificationStore.notify({
        message: "Email sent",
        type: NotificationTypeEnum.success,
        duration: 5000,
      });
      emit("formSubmitted");
    },
    "EmailForm.vue",
    "onSubmit",
    () => {
      notificationStore.notify({
        message: `Error sending email. Please try again.`,
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    }
  );

  formSubmitting.value = false;
};

watchEffect(() => {
  updateSubject();
});

watchEffect(() => {
  emailRecipient.value = props.emailRecipient ?? "";
});

onBeforeMount(() => {
  getCurrentPersonJobAsync();
  updateHolidayMessageAsync();
});
</script>

<style lang="scss">
.email-form {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1rem;
  gap: 16px;

  @include media-breakpoint-up(sm) {
    width: 500px;
  }

  .message-textarea {
    resize: vertical;
  }

  .inline-form-control {
    display: flex;
    margin-bottom: 1rem;
    align-items: center;

    label {
      height: 36px;
      line-height: 24px;
      font-size: 1rem;
      background: #f7f7f7;
      padding: 5px 10px;
      border-radius: 4px 0 0 4px;
      border: 1px solid #ddd;
      padding-right: 24px;
      flex-basis: 25%;
    }

    input {
      height: 36px;
      padding: 0.5rem;
      border: 1px solid #ccc;
      font-size: 1rem;
      border-radius: 0 4px 4px 0;
      width: 100%;
      flex-basis: 75%;
    }
  }

  .k-svg-icon {
    width: 16px;
    height: 16px;
  }

  .holiday-message {
    display: block;
    text-align: center;
    padding: 5px;
    margin: 10px 0;
    background: #fff1d9;
    color: #a99c85;
    border: 1px solid #e8d9c0;
  }
}
</style>
